import Tooltip from '@mui/material/Tooltip';
import { TypographyProps } from '@mui/material/Typography';
import { ReactNode, FC, KeyboardEvent, useState, useEffect } from 'react';

import { gettext } from 'medialoopster/Internationalization';
import { Input } from 'medialoopster/components';

import ButtonGroup from './ButtonGroup';
import OverflowTextField from './OverflowTextField';

const hasValueError = (value: string | null, rule?: string): boolean => {
  if (value && rule) {
    const pattern = RegExp(`^${rule}$`);
    return !pattern.test(value);
  }
  return false;
};
interface InplaceTextEditProps {
  minWidth?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  maxLength?: number;
  id?: string;
  editValue: string;
  textComponents: NonNullable<ReactNode>;
  canEdit: boolean;
  dense?: boolean;
  component?: 'div' | 'p'; // Eg. "span" does not support text overflow ellipse
  validationRule?: string;
  variant?: TypographyProps['variant'];
}

const InplaceTextEdit: FC<InplaceTextEditProps> = ({
  minWidth,
  onChange,
  onSubmit,
  maxLength,
  id = '',
  editValue,
  canEdit,
  textComponents,
  variant = 'body1',
  dense = false,
  component = 'div',
  validationRule = '',
}: InplaceTextEditProps) => {
  const [value, setValue] = useState(editValue);
  const [showError, setShowError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const handleKeys = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !showError) {
      setIsEditing(false);
      onSubmit?.(value);
    }
    if (event.key === 'Escape' || event.key === 'Esc') {
      setIsEditing(false);
    }
  };

  const onEditTitle = () => {
    if (!canEdit) return;
    if (!isEditing) {
      setShowError(hasValueError(value, validationRule));
      setIsEditing(true);
    }
  };

  useEffect(() => {
    setValue(editValue);
  }, [editValue]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const onValueChange = (val: string) => {
    onChange?.(val);
    setValue(val);
    setShowError(hasValueError(val, validationRule));
  };

  const handleSubmit = () => {
    setIsEditing(false);
    setShowError(false);
    setValue(editValue);
    if (value !== editValue) {
      onSubmit?.(value);
    }
  };
  const handleClose = () => {
    setIsEditing(false);
    setValue(editValue);
  };

  return (
    <>
      {!isEditing && (
        <OverflowTextField
          component={component}
          variant={variant}
          onDoubleClick={onEditTitle}
          textComponents={textComponents}
          highlightText={canEdit}
          showPlaceHolder={!editValue && canEdit}
        />
      )}
      {isEditing && (
        <div style={{ display: 'flex' }}>
          <div style={{ minWidth, marginRight: '0.5em' }} data-testid="inputWrapper">
            <Input
              id={id}
              name={id}
              onKeyUp={handleKeys}
              onChange={onValueChange}
              value={value}
              autoFocus
              maxLength={maxLength}
              small
              dense={dense}
              showError={showError}
            />
          </div>
          <Tooltip open={showError} title={showError ? gettext('Please enter valid format') : ''}>
            <ButtonGroup
              onSubmit={handleSubmit}
              onClose={handleClose}
              dense={dense}
              disableSubmit={showError}
            />
          </Tooltip>
        </div>
      )}
    </>
  );
};

export default InplaceTextEdit;
